import React, { useEffect, useState } from "react";

import {
  calculateFootprintingProgress,
  getYearArray,
  isRentenbankProgram,
  queryIsLoading,
  useGetFarms,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprints,
  useGetFootprintingFarmFootprints,
  useLocation,
  useNavigate,
  useTranslation,
} from "utils";

import { AlertBanner, Flex, MainPage, MetaData, PageSpin, TabLabel, Tabs } from "components";

import FootprintingProgressWidget from "pages/app/footprinting/components/FootprintingProgressWidget";
import { FootprintingYearPage } from "pages/app/footprinting/components/FootprintingYearPage";

const Footprinting: React.FC = () => {
  const { t } = useTranslation([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentFarm, isLoading: isGetFarmsLoading } = useGetFarms();

  const navigationStateYear = location.state?.year as number;

  const showRentenbankBanner = isRentenbankProgram(currentFarm?.carbonProgram);

  const currentYear = new Date().getFullYear();

  const availableYears = getYearArray(currentFarm?.carbonProgram?.farmLevelFootprinting?.startYear ?? currentYear, currentYear).filter(
    (year) => !currentFarm?.carbonProgram?.farmLevelFootprinting?.disabledYears?.includes(year)
  );
  const [selectedYear, setSelectedYear] = useState(navigationStateYear ?? currentFarm?.carbonProgram?.farmLevelFootprinting?.startYear ?? currentYear);

  const farmProgressQueries = useGetFootprintingFarmFootprints(currentFarm?.id, availableYears);
  const cropProgressQueries = useGetFootprintingCropFootprints(
    currentFarm?.id,
    availableYears,
    currentFarm?.carbonProgram?.farmLevelFootprinting?.programCrops
  );
  const dairyProgressQueries = useGetFootprintingDairyFootprints(currentFarm?.id, availableYears);

  /** Set our navigation state before leaving the page so when somebody uses
   * the back button we remember which year tab is selected
   */
  useEffect(() => {
    navigate(".", { replace: true, state: { year: selectedYear } });
  }, [navigate, selectedYear]);

  if (
    !currentFarm ||
    !currentFarm?.id ||
    isGetFarmsLoading ||
    !currentFarm?.carbonProgram ||
    farmProgressQueries.some(queryIsLoading) ||
    cropProgressQueries.some(queryIsLoading) ||
    dairyProgressQueries.some(queryIsLoading)
  ) {
    return (
      <Flex justify="center">
        <PageSpin />
      </Flex>
    );
  }

  if (currentFarm?.carbonProgram.farmLevelFootprinting === undefined || currentFarm?.carbonProgram.platformFeatures.footprinting?.enabled === false) {
    navigate("/home");
  }

  const navigationCrumbs = {
    crumbs: [
      { name: currentFarm?.name || t("modules:navigation.overview.farm"), replacedByFarmSwitch: true },
      { name: t("footprinting:footprintingLandingPage.breadcrumb") },
    ],
  };

  const yearsToIsSubmitted = availableYears.reduce(
    (acc, year) => {
      // @ts-expect-error TS doesn't know that the `.map` catches all the undefined data
      acc[year] = calculateFootprintingProgress(farmProgressQueries.find((query) => query.data?.year === year)?.data, [
        ...cropProgressQueries.filter((query) => !!query.data && query.data?.year === year).map((q) => q.data),
        ...dairyProgressQueries.filter((query) => !!query.data && query.data?.year === year).map((q) => q.data),
      ]).isSubmitted;
      return acc;
    },
    {} as Record<number, boolean>
  );

  return (
    <MainPage pageName="Footprinting" navigationCrumbs={navigationCrumbs} showMobileAlert fullWidth>
      <MetaData title={t("footprinting:footprintingPage")} />
      {showRentenbankBanner ? (
        <Flex>
          <AlertBanner
            closeable
            className="margin-top-100 margin-left-500 margin-right-500 margin-bottom-200"
            type="info"
            iconName="tabler/info-circle"
            iconSize={24}
            iconAlign="start"
            title={t("home:rentenbankBanner.title")}
            description={t("home:rentenbankBanner.description")}
          />
        </Flex>
      ) : null}
      <Flex className="footprinting-sidebar container" justify="space-between">
        <Flex vertical className="main-container flex-1 margin-left-500 margin-right-500">
          <Tabs
            className="padding-top-200 padding-left-300"
            activeKey={selectedYear.toString()}
            size="large"
            onChange={(k) => setSelectedYear(parseInt(k))}
            items={availableYears.map((y) => ({
              label: (
                <TabLabel dotColor="orange" showDot={!yearsToIsSubmitted[y]}>
                  {y.toString()}
                </TabLabel>
              ),
              key: y.toString(),
              children: (
                <FootprintingYearPage
                  year={y}
                  farmId={currentFarm?.id ?? ""}
                  cropsWithFootprintForYear={cropProgressQueries
                    .filter((cpq) => cpq.data !== undefined && cpq.data.footprint !== undefined && cpq.data.year === selectedYear)
                    .map((cpq) => cpq.data?.cropId ?? "")}
                  yearIsSubmitted={yearsToIsSubmitted[y]}
                />
              ),
            }))}
          />
        </Flex>
        <Flex className="sidebar side-bar-footprinting full-width" vertical>
          <FootprintingProgressWidget carbonProgram={currentFarm?.carbonProgram ?? undefined} farmId={currentFarm?.id ?? ""} year={selectedYear} />
        </Flex>
      </Flex>
    </MainPage>
  );
};

export default Footprinting;
